import { extractId } from "@repo/shopify/src/utils";
import { OrderStatusButtonType } from "@repo/shopify/src/utils/order";
import React from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";

import Button from "@atoms/Button";
import Icon from "@atoms/Icon";

import useLayout from "@hooks/reducerHooks/useLayout";
import useUser from "@hooks/reducerHooks/useUser";
import useOrders from "@hooks/useOrders";

import { IconName } from "@const/enum";
import { URLS } from "@const/urls";

import { fetchCancelOrder } from "@api/lamdaApi";

interface IProps {
  id: string;
  name: string;
  amount: string;
  buttonType: OrderStatusButtonType;
  isDrawOrder?: boolean;
}

export default function OrderStatusButton({ id, name, amount, buttonType, isDrawOrder }: IProps) {
  const { accessToken, customer, fetchUser } = useUser();
  const { openModal, setModalProgress, closeModal } = useLayout();
  const { refetch } = useOrders();

  const navigate = useNavigate();
  const { t } = useTranslation();

  const onCancelOrder = async () => {
    const response = await fetchCancelOrder({
      customerId: extractId(customer.id),
      orderId: extractId(id),
      amount,
    });

    if (response) {
      await refetch();
      await fetchUser({ variables: { customerAccessToken: accessToken } });
    }

    setModalProgress(false);
    closeModal();
  };

  const openCancelModal = (e: React.MouseEvent<HTMLElement>) => {
    e.preventDefault();

    openModal({
      title: "CANCEL ORDER",
      description: t(`${name}${t("account.order.cancel_ask")}`),
      cancelButton: { label: "Cancel" },
      confirmButton: {
        label: "Okay",
        onClick: () => {
          setModalProgress(true);
          void onCancelOrder();
        },
      },
    });
  };

  // TODO: 드로우 주문인지 아닌지 로직 나누기
  return (
    <>
      {/* 주문 취소 버튼 */}
      {/**
       * 드로우 당첨이 되면, 드로우 응모 내역 뿐만 아니라 주문 내역에도 해당 주문이 노출됨
       * 따라서 Draw 주문인 경우 취소 버튼이 보이지 않도록 외부에서 처리해줌
       * -> 드로우인 주문인 경우 무조건 노출시키지 않음
       */}
      {buttonType === OrderStatusButtonType.CANCEL && !isDrawOrder ? (
        <Button className="relative w-fit" onClick={openCancelModal} styleType="secondary" variant="contained">
          {t("account.order.cancel_order")}
          <Icon alt="close_100" name={IconName.Close_100} width="14" />
        </Button>
      ) : null}

      {/* 반품 신청 버튼 */}
      {/**
       * - 반품 가능 여부는 다른 조건을 기준으로 판단하고, 반품 상태(order.returnStatus)는 고려하지 않음
       * - why? 여러 개의 상품이 포함된 주문에서 일부 상품만 반품 신청을 했을 경우, 전체 주문의 반품 상태를 파악하는 것이 어려움
       * - 따라서 버튼은 반품 상태에 관계없이 반품 가능한 상품이 있는 경우 노출하고,
       *   '반품 요청' 페이지로 이동 후 해당 페이지에서 반품 가능 여부를 다시 확인하도록 함
       * - 이렇게 하면 fetchReturnableOrder를 매번 호출할 필요가 없음
       * 1. FULFILLED 상태
       * 2. Refunded 가 아니어야 함
       * 3. 주문이 생성된 이후부터 9일 동안만 노출
       * */}
      {buttonType === OrderStatusButtonType.RETURN ? (
        <Button
          className="relative w-fit"
          onClick={(e) => {
            e.preventDefault();
            navigate(`${URLS.ACCOUNT_RETURN_REQUEST_DETAIL}/${extractId(id)}`, {
              state: {
                orderId: extractId(id),
              },
            });
          }}
          styleType="secondary"
          variant="contained"
        >
          {t("account.order.return_order")}
          <Icon alt="return_100" name={IconName.Return_100} width="14" />
        </Button>
      ) : null}
    </>
  );
}
